import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import LockOutlined from '@ant-design/icons/LockOutlined';
import { adminApi } from '../api/admin/admin.api';

export default function MfaSettingsDialog({ open, onClose }) {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState('idle'); // idle | setup | verify | disabling
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      loadStatus();
    }
  }, [open]);

  const loadStatus = async () => {
    try {
      const res = await adminApi.mfaStatus();
      setStatus(res.data);
      setStep('idle');
    } catch {
      setError('Failed to load MFA status');
    }
  };

  const handleSetup = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await adminApi.mfaSetup();
      setOtpauthUrl(res.data.otpauthUrl);
      setSecret(res.data.secret);
      setStep('verify');
    } catch (err) {
      setError(err?.response?.data?.message || 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  const handleEnable = async () => {
    setBusy(true);
    setError('');
    try {
      await adminApi.mfaEnable({ code });
      setStatus({ mfaEnabled: true });
      setStep('idle');
      setCode('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    setError('');
    try {
      await adminApi.mfaDisable({ password });
      setStatus({ mfaEnabled: false });
      setStep('idle');
      setPassword('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid password');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setStep('idle');
    setCode('');
    setPassword('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockOutlined />
        Two-Factor Authentication (TOTP)
      </DialogTitle>
      <DialogContent>
        {status === null && <CircularProgress size={24} />}
        {status && step === 'idle' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity={status.mfaEnabled ? 'success' : 'info'}>
              {status.mfaEnabled
                ? 'Two-factor authentication is enabled. You must enter a code from your authenticator app on every sign-in.'
                : 'Two-factor authentication is not enabled. Enable it to require a TOTP code on every admin sign-in.'}
            </Alert>
            {status.mfaEnabled ? (
              <Button variant="outlined" color="error" onClick={() => setStep('disabling')}>
                Disable 2FA
              </Button>
            ) : (
              <Button variant="contained" onClick={handleSetup} disabled={busy}>
                {busy ? <CircularProgress size={20} /> : 'Set Up 2FA'}
              </Button>
            )}
          </Stack>
        )}

        {step === 'verify' && (
          <Stack spacing={2} sx={{ mt: 1, alignItems: 'center' }}>
            <Typography variant="body2">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
            </Typography>
            {otpauthUrl && (
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <QRCodeCanvas value={otpauthUrl} size={200} />
              </Box>
            )}
            <Typography variant="caption" sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
              Or enter manually: <strong>{secret}</strong>
            </Typography>
            <TextField
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputProps={{ maxLength: 6, style: { letterSpacing: '0.3em', textAlign: 'center' } }}
              fullWidth
            />
            <Button variant="contained" onClick={handleEnable} disabled={busy || code.length !== 6}>
              {busy ? <CircularProgress size={20} /> : 'Verify & Enable'}
            </Button>
          </Stack>
        )}

        {step === 'disabling' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Disabling 2FA reduces account security. Enter your password to confirm.
            </Alert>
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            <Button variant="contained" color="error" onClick={handleDisable} disabled={busy || !password}>
              {busy ? <CircularProgress size={20} /> : 'Confirm Disable'}
            </Button>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
